import type { CasaLeonWorldSnapshot } from '@casa-leon-vivo/contracts/casa-leon';
import type { FloorLayoutModule } from '../floor/dynamic-floor.js';
import type { GuestMarker } from '../guests/guest-simulation.js';
import type { WeatherVisualState } from '../weather/weather-types.js';
import type { CameraPresetId } from './camera-presets.js';
import type { DecorPlacement, TableLayoutOffset } from '../editor/layout-types.js';

export type VivoAppMode = 'simulation' | 'edit';

export type CasaLeon3DZone =
  | 'entrada'
  | 'salon'
  | 'patio'
  | 'cocina'
  | 'parrilla'
  | 'barra'
  | 'caja';

export type CasaLeon3DInteractiveId =
  | 'kitchen-board'
  | 'whatsapp-sign'
  | 'phone'
  | 'register'
  | 'weather-station'
  | `table-${string}`;

export interface CasaLeon3DInteractiveFocus {
  id: CasaLeon3DInteractiveId;
  zone: CasaLeon3DZone;
  label: string;
}

export interface CasaLeon3DSimPanels {
  kitchenOrders: { id: string; table: string; items: string[]; status: string; etaMin: number }[];
  whatsapp: { from: string; text: string; bot: boolean; at: string }[];
  phone: { id: string; caller: string; reason: string; state: 'ringing' | 'active' | 'ended' };
  register: { open: boolean; queue: { table: string; total: number; method: string }[]; salesToday: number };
}

export interface CasaLeon3DPerfStats {
  fps: number;
  frameTimeMs: number;
  drawCalls: number;
  triangles: number;
  dpr: number;
  preserveDrawingBuffer: boolean;
}

export interface LayoutEditorSnapResult {
  worldX: number;
  worldZ: number;
}

export interface CasaLeon3DLayoutEditorHandlers {
  snapModule?: (moduleId: string, worldX: number, worldZ: number) => LayoutEditorSnapResult | null;
  onModuleDrop?: (moduleId: string, worldX: number, worldZ: number) => void;
}

export interface CasaLeon3DCallbacks {
  onInteractiveFocus?: (focus: CasaLeon3DInteractiveFocus) => void;
  onZoneHover?: (zone: CasaLeon3DZone | null) => void;
  onSimPanelsUpdate?: (panels: CasaLeon3DSimPanels) => void;
  onPerfUpdate?: (stats: CasaLeon3DPerfStats) => void;
  onCharactersReady?: () => void;
  onFrame?: (dt: number) => void;
}

export interface CasaLeon3DExperience {
  setSnapshot(snapshot: CasaLeonWorldSnapshot): void;
  setSoundscapeEnabled(enabled: boolean): void;
  unlockAudio(): Promise<void>;
  focusInteractive(id: CasaLeon3DInteractiveId): void;
  setFloorLayout(layout: FloorLayoutModule[]): void;
  setWeatherVisual(state: WeatherVisualState): void;
  setGuestMarkers(markers: GuestMarker[]): void;
  setCameraPreset(preset: CameraPresetId): void;
  getPerf(): CasaLeon3DPerfStats | null;
  setAppMode(mode: VivoAppMode): void;
  setLayoutEditorState(offsets: TableLayoutOffset[], decor: DecorPlacement[]): void;
  setLayoutEditorHandlers(handlers: CasaLeon3DLayoutEditorHandlers): void;
  getSelectedLayoutModuleId(): string | null;
  restartPremiumPilot(): void;
  setPremiumPilotLayout(layout: FloorLayoutModule[]): void;
  resetCamera(): void;
  sampleCanvasBrightness(): number;
  resize(): void;
  destroy(): void;
}

export const INTERACTIVE_USER_DATA_KEY = 'mvInteractiveId';
